import { NavLink } from "react-router-dom";
import { TAB_LINKS } from "./navLinks";

/** Bottom tab bar, mobile only. Practice is the raised center mic button —
 *  the app's primary action — with the other four destinations flanking it.
 *  Rendered as a flex sibling below the routed page (pages scroll internally),
 *  hidden while a voice session is live so it never competes with the mic
 *  controls. */
export default function MobileTabBar({ hidden = false }: { hidden?: boolean }) {
  if (hidden) return null;

  const practice = TAB_LINKS.find((l) => l.to === "/")!;
  const others = TAB_LINKS.filter((l) => l.to !== "/");
  const left = others.slice(0, 2);
  const right = others.slice(2);

  const renderTab = ({ to, label, end, icon }: (typeof others)[number]) => (
    <NavLink
      key={to}
      to={to}
      end={end}
      className={({ isActive }) =>
        `relative flex flex-1 flex-col items-center gap-1 pt-2 pb-1.5 no-underline transition-colors ${
          isActive ? "text-ink" : "text-text-tertiary"
        }`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute top-0 left-1/2 -translate-x-1/2 h-[3px] w-8 rounded-b-full bg-success" />
          )}
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {icon}
          </svg>
          <span className="text-[0.625rem] font-medium leading-none">{label}</span>
        </>
      )}
    </NavLink>
  );

  return (
    <nav
      className="sm:hidden flex items-stretch flex-shrink-0 border-t border-border bg-surface pb-[max(env(safe-area-inset-bottom)-14px,0px)]"
      aria-label="Primary"
    >
      {left.map(renderTab)}

      {/* Practice: raised mic button floating above the bar */}
      <div className="relative flex-1 flex justify-center">
        <NavLink
          to={practice.to}
          end={practice.end}
          aria-label={practice.label}
          className="absolute -top-6 flex items-center justify-center w-12 h-12 rounded-full no-underline transition-all bg-ink text-cream border-4 border-surface shadow-[0_6px_18px_rgba(0,0,0,0.45)] active:scale-95"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {practice.icon}
          </svg>
        </NavLink>
        <NavLink
          to={practice.to}
          end={practice.end}
          tabIndex={-1}
          aria-hidden="true"
          className={({ isActive }) =>
            `self-end pb-1.5 text-[0.8rem] font-medium leading-none no-underline ${
              isActive ? "text-ink" : "text-text-tertiary"
            }`
          }
        >
          {practice.label}
        </NavLink>
      </div>

      {right.map(renderTab)}
    </nav>
  );
}
